/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { UnsavedWorkGuardProvider } from './components/ui/UnsavedWorkGuard';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AUDIT_LOG_KEY } from './hooks/useAudit';
import { ChakraProvider } from '@chakra-ui/react';
import { system } from './theme';
import { ColorModeProvider } from './components/ui/color-mode';
import { AppLayout } from './components/layout';
import { ProtectedRoute, LoadingSpinner } from './components/ui';
import { useAuthStore } from './store';
import { authService } from './services';

// Eager load login/unauthorized
import { LoginPage } from './pages/LoginPage';
import { UnauthorizedPage } from './pages/UnauthorizedPage';
import { DesignTokensPage } from './pages/DesignTokensPage';
import { OrtbBodyEditorDevPage } from './pages/OrtbBodyEditorDevPage';

// Lazy load all main pages for code splitting
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const DemandSourcesPage = lazy(() =>
  import('./pages/DemandSourcesPage').then((m) => ({ default: m.DemandSourcesPage })),
);
const DemandSourceDetailPage = lazy(() =>
  import('./pages/DemandSourceDetailPage').then((m) => ({ default: m.DemandSourceDetailPage })),
);
const UsersPage = lazy(() => import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const ServicesPage = lazy(() => import('./pages/ServicesPage').then((m) => ({ default: m.ServicesPage })));
const ConfigPage = lazy(() => import('./pages/ConfigPage').then((m) => ({ default: m.ConfigPage })));
const AuditPage = lazy(() => import('./pages/AuditPage').then((m) => ({ default: m.AuditPage })));
const ReqStrategiesPage = lazy(() =>
  import('./pages/ReqStrategiesPage').then((m) => ({ default: m.ReqStrategiesPage })),
);
const ReqStrategyDetailPage = lazy(() =>
  import('./pages/ReqStrategyDetailPage').then((m) => ({ default: m.ReqStrategyDetailPage })),
);
const OptimizationStrategiesPage = lazy(() =>
  import('./pages/OptimizationStrategiesPage').then((m) => ({ default: m.OptimizationStrategiesPage })),
);
const SystemBackupPage = lazy(() => import('./pages/SystemBackupPage').then((m) => ({ default: m.SystemBackupPage })));
const ApiExplorerPage = lazy(() => import('./pages/ApiExplorerPage').then((m) => ({ default: m.ApiExplorerPage })));
const MetricsExportPage = lazy(() =>
  import('./pages/MetricsExportPage').then((m) => ({ default: m.MetricsExportPage })),
);
const MetricsConsolePage = lazy(() =>
  import('./pages/MetricsConsolePage').then((m) => ({ default: m.MetricsConsolePage })),
);
const SessionsPage = lazy(() => import('./pages/SessionsPage').then((m) => ({ default: m.SessionsPage })));
const RequestTracerPage = lazy(() =>
  import('./pages/RequestTracerPage').then((m) => ({ default: m.RequestTracerPage })),
);
const PixelsPage = lazy(() => import('./pages/PixelsPage').then((m) => ({ default: m.PixelsPage })));
const RuleSetsPage = lazy(() => import('./pages/RuleSetsPage').then((m) => ({ default: m.RuleSetsPage })));
const GuidancePage = lazy(() => import('./pages/GuidancePage').then((m) => ({ default: m.GuidancePage })));
const StrategySelectorPage = lazy(() =>
  import('./pages/StrategySelectorPage').then((m) => ({ default: m.StrategySelectorPage })),
);
const DemoModulePage = lazy(() => import('./pages/DemoModulePage').then((m) => ({ default: m.DemoModulePage })));
const FieldCatalogPage = lazy(() => import('./pages/FieldCatalogPage').then((m) => ({ default: m.FieldCatalogPage })));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage').then((m) => ({ default: m.TemplatesPage })));
const PipelineDiagnosticsPage = lazy(() =>
  import('./pages/PipelineDiagnosticsPage').then((m) => ({ default: m.PipelineDiagnosticsPage })),
);
const ExpressionLibraryPage = lazy(() =>
  import('./pages/ExpressionLibraryPage').then((m) => ({ default: m.ExpressionLibraryPage })),
);
const ErrorLogsPage = lazy(() => import('./pages/ErrorLogsPage').then((m) => ({ default: m.ErrorLogsPage })));
const AlertsPage = lazy(() => import('./pages/AlertsPage').then((m) => ({ default: m.AlertsPage })));

const RoleDefaultRedirect: React.FC = () => {
  return <Navigate to="/dashboard" replace />;
};

const queryClient: QueryClient = new QueryClient({
  // **Every write may have recorded activity** (`#1570`): the server audits each artifact mutation, so a
  // Recent Activity panel is stale after any successful one. Said once here rather than in each
  // mutation hook, where the next hook would be the one that forgot.
  mutationCache: new MutationCache({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [AUDIT_LOG_KEY] });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Inner component that handles startup session restore via GET /api/auth/me.
 * Must be rendered inside QueryClientProvider so it can use hooks if needed.
 */
const AppRoutes: React.FC = () => {
  const { updateUser, setLoading } = useAuthStore();

  useEffect(() => {
    // Session restore runs once on mount to validate any persisted JWT.
    // Intentionally omits token/updateUser/setLoading from deps: this check
    // should only run at startup, not on subsequent token changes (e.g. after login).
    const storedToken = useAuthStore.getState().token;
    if (!storedToken) {
      setLoading(false);
      return;
    }
    authService
      .getCurrentUser()
      .then((user) => {
        updateUser(user);
        setLoading(false);
      })
      .catch(() => {
        // Non-401 errors (e.g. network issues): clear loading, keep auth state.
        // 401 errors are handled by the api.ts interceptor (clearAuth + redirect).
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      {/* Design-token reference (W1.4 / #679) — the primitive surface for the theme layer */}
      <Route path="/design-tokens" element={<DesignTokensPage />} />
      {/* Dev/design harness for the ORTB body editor (#858) — not part of product IA */}
      <Route path="/dev/ortb-body-editor" element={<OrtbBodyEditorDevPage />} />

      {/* Protected routes with shared layout */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RoleDefaultRedirect />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'dashboard', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <DashboardPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/demo"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'demo_module', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <DemoModulePage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/demand-sources"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'demand_sources', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <DemandSourcesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/demand-sources/:id"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'demand_sources', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <DemandSourceDetailPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'analytics', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <AnalyticsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/services"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'services', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <ServicesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/config"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'config', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <ConfigPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'users', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <UsersPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <UsersPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'audit', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <AuditPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/req-strategies"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'req_strategies', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <ReqStrategiesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/req-strategies/:id"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'req_strategies', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <ReqStrategyDetailPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/opt-strategies"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'opt_strategies', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <OptimizationStrategiesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/system-backup"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'system_backup', action: 'manage' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <SystemBackupPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/api-explorer"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'api_explorer', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <ApiExplorerPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/metrics-export"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'metrics_export', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <MetricsExportPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/metrics-console"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'metrics_console', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <MetricsConsolePage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sessions"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'sessions', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <SessionsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/request-tracer"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'request_tracer', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <RequestTracerPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pixels"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'pixels', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <PixelsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/rule-sets"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'rule_sets', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <RuleSetsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/guidance"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'guidance', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <GuidancePage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/strategy-selectors"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'strategy_selectors', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <StrategySelectorPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/field-catalog"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'field_catalog', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <FieldCatalogPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/*
          Templates read under `demand_sources` rather than a permission of their own (`#1090`).
          A template *is* a demand-source artifact, so anyone who may read the sources may read the
          baselines they were built from, and inventing a `templates` resource for a surface holding
          one type would be a vocabulary decided before there is anything to decide it against. When
          the surface holds several types, the question becomes which of their read rights it takes —
          which is a real design point, and one to answer with the second type in front of us.
        */}
        <Route
          path="/templates"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'demand_sources', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <TemplatesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pipeline-diagnostics"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'pipeline_diagnostics', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <PipelineDiagnosticsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/expression-library"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'expression_library', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <ExpressionLibraryPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/error-logs"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'error_logs', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <ErrorLogsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/alerts"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'alert_subscriptions', action: 'read' }}>
              <Suspense fallback={<LoadingSpinner />}>
                <AlertsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<RoleDefaultRedirect />} />
    </Routes>
  );
};

export const App: React.FC = () => (
  <ChakraProvider value={system}>
    <ColorModeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {/* **Inside the router, above every surface** (`#1245`). Inside, because the guard navigates;
              above, because a nav guard that only wrapped one page would be the scoping this closes. */}
          <UnsavedWorkGuardProvider>
            <AppRoutes />
          </UnsavedWorkGuardProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ColorModeProvider>
  </ChakraProvider>
);

export default App;
